/**
 * CommandPalette — Cmd+K / Ctrl+K quick navigation (P5, DEC-063).
 *
 * Search space: flattened navigation index (every sidebar leaf, which
 * includes every entity page), fixed commands (overview, dashboards,
 * events, settings) and optional per-module Studio links when
 * VITE_STUDIO_URL is configured. Navigation-only by design — no
 * data-mutating commands in v1. Recent picks rank first (localStorage).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon, ArrowRight, ExternalLink } from 'lucide-react';
import {
  buildSearchIndex,
  MODULE_LIST,
  type FlatSearchEntry,
} from '../registry/portalRegistry';

const RECENTS_KEY = 'portal_palette_recents';
const MAX_RESULTS = 12;

interface PaletteEntry extends FlatSearchEntry {
  /** External URLs open in a new tab instead of navigating. */
  external?: boolean;
}

function loadRecents(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

function pushRecent(path: string) {
  try {
    const next = [path, ...loadRecents().filter((p) => p !== path)].slice(0, 10);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch { /* storage blocked */ }
}

function buildEntries(): PaletteEntry[] {
  const entries: PaletteEntry[] = [];

  // Fixed commands first so they win ties.
  entries.push({ label: 'Global overview', path: '/dashboard', section: 'Go to' });
  entries.push({ label: 'Events', path: '/events', section: 'Go to' });
  entries.push({ label: 'Settings', path: '/settings', section: 'Go to' });
  for (const m of MODULE_LIST) {
    entries.push({ label: `${m.label} dashboard`, path: `/${m.id}/dashboard`, section: 'Go to' });
  }

  // Every sidebar leaf — includes every entity page.
  entries.push(...buildSearchIndex());

  // Studio links (design-time tool; external, config-gated).
  const studioUrl = (import.meta.env?.VITE_STUDIO_URL as string | undefined)?.replace(/\/$/, '');
  if (studioUrl) {
    for (const m of MODULE_LIST) {
      entries.push({
        label: `Open in Studio — ${m.label}`,
        path: `${studioUrl}/?module=${m.id}`,
        section: 'Studio',
        external: true,
      });
    }
  }

  // De-dup by path (nav index may repeat fixed paths).
  const seen = new Set<string>();
  return entries.filter((e) => (seen.has(e.path) ? false : (seen.add(e.path), true)));
}

function scoreEntry(entry: PaletteEntry, query: string, recents: string[]): number {
  const q = query.trim().toLowerCase();
  const label = entry.label.toLowerCase();
  let score = -1;
  if (!q) {
    score = 1; // empty query: everything eligible, recents decide
  } else if (label === q) {
    score = 100;
  } else if (label.startsWith(q)) {
    score = 80;
  } else if (label.includes(q)) {
    score = 60;
  } else {
    // all query tokens must appear somewhere in label+section
    const hay = `${label} ${entry.section.toLowerCase()}`;
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.every((t) => hay.includes(t))) score = 40;
  }
  if (score < 0) return score;
  const recencyIdx = recents.indexOf(entry.path);
  if (recencyIdx >= 0) score += 20 - recencyIdx;
  return score;
}

export const CommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLUListElement | null>(null);

  const entries = useMemo(buildEntries, []);
  const recents = useMemo(loadRecents, [open]);

  const results = useMemo(() => {
    return entries
      .map((e) => ({ e, s: scoreEntry(e, query, recents) }))
      .filter((r) => r.s >= 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, MAX_RESULTS)
      .map((r) => r.e);
  }, [entries, query, recents]);

  useEffect(() => setCursor(0), [query, open]);

  // Global shortcut
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'k') {
        ev.preventDefault();
        // Capture-phase + stopPropagation: the palette owns Cmd+K even when
        // other widgets (e.g. the FORGE FAB) bind the same shortcut downstream.
        ev.stopPropagation();
        setOpen((o) => !o);
        setQuery('');
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const run = (entry: PaletteEntry) => {
    setOpen(false);
    pushRecent(entry.path);
    if (entry.external) {
      window.open(entry.path, '_blank', 'noopener');
    } else {
      navigate(entry.path);
    }
  };

  const onInputKey = (ev: React.KeyboardEvent) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (ev.key === 'Enter' && results[cursor]) {
      ev.preventDefault();
      run(results[cursor]);
    } else if (ev.key === 'Escape') {
      setOpen(false);
    }
  };

  // Keep the active row in view while arrowing.
  useEffect(() => {
    const el = listRef.current?.children[cursor] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: -20, borderRadius: 2 } }}
      data-testid="command-palette"
    >
      <Box sx={{ p: 1.5, pb: 0.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Go to page, entity, dashboard…"
          value={query}
          onChange={(ev) => setQuery(ev.target.value)}
          onKeyDown={onInputKey}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={16} />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'data-testid': 'command-palette-input' }}
        />
      </Box>
      <List ref={listRef} dense sx={{ maxHeight: 380, overflowY: 'auto', px: 0.5, pb: 1 }}>
        {results.map((r, i) => (
          <ListItemButton
            key={`${r.path}`}
            selected={i === cursor}
            onClick={() => run(r)}
            onMouseEnter={() => setCursor(i)}
            sx={{ borderRadius: 1, mx: 0.5 }}
            data-testid={`command-palette-item-${i}`}
          >
            <ListItemText
              primary={r.label}
              secondary={r.external ? r.path : undefined}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
            />
            <Chip label={r.section} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, mr: 1 }} />
            {r.external ? <ExternalLink size={13} /> : <ArrowRight size={13} />}
          </ListItemButton>
        ))}
        {results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
            No matches.
          </Typography>
        )}
      </List>
    </Dialog>
  );
};

export default CommandPalette;
